import React, { useEffect, useMemo, useState } from "react";
import { FileText } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import {
  createTenantPolicy,
  getTenantPolicyVersionDocumentUrl,
  listTenantPoliciesRegister,
  listTenantPolicyVersions,
} from "../../api/tenantPolicies.api";
import Loader from "../../components/common/Loader";
import PolicyDocumentModal from "../../components/policies/PolicyDocumentModal";
import Button from "../../ui/Button";
import Input from "../../ui/Input";
import Modal from "../../ui/Modal";
import Select from "../../ui/Select";
import StatusBadge from "../../ui/StatusBadge";
import Table from "../../ui/Table";
import { spacing } from "../../ui/tokens";
import TenantPolicyDetailDrawer from "./TenantPolicyDetailDrawer";

const PAGE_SIZE = 20;

const emptyCardStyle = {
  border: "1px dashed #d1d5db",
  borderRadius: 12,
  padding: spacing.xl,
  background: "#f9fafb",
  display: "grid",
  gap: spacing.sm,
  maxWidth: 520,
};

const StatusIndicator = ({ status }) => {
  const normalized = typeof status === "string" ? status.toLowerCase() : "";
  const isRetired = normalized === "retired";
  return (
    <StatusBadge
      variant="kpi"
      value={isRetired ? "stale" : "green"}
      label={isRetired ? "Retired" : "Active"}
    />
  );
};

const PublicationIndicator = ({ published }) => (
  <StatusBadge value={published ? "published" : "draft"} label={published ? "Published" : "Unpublished"} />
);

const iconButtonStyle = {
  width: 34,
  height: 34,
  padding: 0,
  borderRadius: 6,
};

const documentIconWrapStyle = {
  position: "relative",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 18,
  height: 18,
};

const documentUnavailableSlashStyle = {
  position: "absolute",
  width: 22,
  height: 2,
  borderRadius: 999,
  background: "currentColor",
  transform: "rotate(-45deg)",
  opacity: 0.9,
};

const byDescendingVersion = (left, right) => Number(right?.version || 0) - Number(left?.version || 0);

const hasAttachedDocument = (version) => Boolean(version?.document_filename);

const selectRegisterDocumentVersion = (versions, row) => {
  if (!Array.isArray(versions) || versions.length === 0) return null;

  const latestDraftWithDocument = versions.find(
    (version) =>
      Number(version?.id) === Number(row?.latest_draft_version_id) && hasAttachedDocument(version)
  );
  if (latestDraftWithDocument) return latestDraftWithDocument;

  const publishedWithDocument = versions.find(
    (version) =>
      Number(version?.id) === Number(row?.published_version_id) && hasAttachedDocument(version)
  );
  if (publishedWithDocument) return publishedWithDocument;

  return [...versions].sort(byDescendingVersion).find(hasAttachedDocument) || null;
};

export default function PoliciesRegister() {
  const navigate = useNavigate();
  const { tenantPolicyId: routeTenantPolicyId } = useParams();
  const [policies, setPolicies] = useState([]);
  const [queryInput, setQueryInput] = useState("");
  const [appliedQuery, setAppliedQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [publishedFilter, setPublishedFilter] = useState("");
  const [reviewDueFilter, setReviewDueFilter] = useState("");
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createDescription, setCreateDescription] = useState("");
  const [createOwner, setCreateOwner] = useState("");
  const [createError, setCreateError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [documentViewer, setDocumentViewer] = useState(null);
  const [documentError, setDocumentError] = useState(null);
  const [loadingDocumentPolicyId, setLoadingDocumentPolicyId] = useState(null);
  const drawerPolicyId = routeTenantPolicyId || null;

  const loadPolicies = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listTenantPoliciesRegister({
        q: appliedQuery || undefined,
        status: statusFilter || undefined,
        published:
          publishedFilter === "" ? undefined : publishedFilter === "published",
        review_due: reviewDueFilter === "" ? undefined : reviewDueFilter === "due",
        limit: PAGE_SIZE,
        offset,
      });
      setPolicies(Array.isArray(data?.items) ? data.items : []);
      setTotal(Number.isFinite(data?.total) ? data.total : 0);
    } catch (err) {
      setError(err?.message || "Failed to load policies");
      setPolicies([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPolicies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedQuery, statusFilter, publishedFilter, reviewDueFilter, offset]);

  useEffect(() => {
    const handler = () => setShowAddModal(true);
    window.addEventListener("openAddPolicy", handler);
    return () => window.removeEventListener("openAddPolicy", handler);
  }, []);

  useEffect(() => {
    if (!showAddModal) return;
    setCreateName("");
    setCreateDescription("");
    setCreateOwner("");
    setCreateError(null);
  }, [showAddModal]);

  const handleOpenAttachedDocument = async (event, row) => {
    event.stopPropagation();
    const tenantPolicyId = row?.tenant_policy_id;
    if (!tenantPolicyId) return;

    setDocumentError(null);
    setLoadingDocumentPolicyId(tenantPolicyId);
    try {
      const versions = await listTenantPolicyVersions(tenantPolicyId);
      const latestVersion = selectRegisterDocumentVersion(versions, row);

      if (!latestVersion?.document_filename) {
        throw new Error("No attached policy document is available for this policy.");
      }

      setDocumentViewer({
        tenantPolicyId,
        version: latestVersion,
      });
    } catch (err) {
      setDocumentError(err?.message || "Failed to open policy document.");
    } finally {
      setLoadingDocumentPolicyId(null);
    }
  };

  const columns = useMemo(
    () => [
      { header: "Name", accessor: "name" },
      {
        header: "Owner",
        accessor: (row) => row.owner_id || "Unassigned",
      },
      {
        header: "Status",
        accessor: (row) => <StatusIndicator status={row.policy_status} />,
      },
      {
        header: "Publication",
        accessor: (row) => <PublicationIndicator published={row.published_version_exists} />,
      },
      {
        id: "document",
        header: "Document",
        align: "center",
        cellStyle: { width: 96 },
        accessor: (row) => {
          const documentAvailable = row?.document_available !== false;
          const actionLabel = documentAvailable
            ? `Open attached policy document for ${row.name || "policy"}`
            : `No attached policy document available for ${row.name || "policy"}`;

          return (
            <Button
              type="button"
              variant="ghost"
              disabled={!documentAvailable}
              loading={documentAvailable && loadingDocumentPolicyId === row.tenant_policy_id}
              onClick={(event) => handleOpenAttachedDocument(event, row)}
              onKeyDown={(event) => event.stopPropagation()}
              aria-label={actionLabel}
              title={actionLabel}
              style={iconButtonStyle}
            >
              <span style={documentIconWrapStyle} aria-hidden="true">
                <FileText size={18} />
                {!documentAvailable ? <span style={documentUnavailableSlashStyle} /> : null}
              </span>
            </Button>
          );
        },
      },
    ],
    [loadingDocumentPolicyId]
  );

  const totalPages = Math.max(1, Math.ceil((total || 0) / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const hasActiveFilters = Boolean(
    appliedQuery || statusFilter || publishedFilter || reviewDueFilter
  );

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setOffset(0);
    setAppliedQuery(queryInput.trim());
  };

  const handleClearFilters = () => {
    setQueryInput("");
    setAppliedQuery("");
    setStatusFilter("");
    setPublishedFilter("");
    setReviewDueFilter("");
    setOffset(0);
  };

  const handleCreate = async () => {
    const name = createName.trim();
    if (!name) {
      setCreateError("Name is required.");
      return;
    }

    setCreateError(null);
    setCreating(true);
    try {
      await createTenantPolicy({
        name,
        description: createDescription.trim() || null,
        owner_id: createOwner.trim() || null,
      });
      setShowAddModal(false);
      if (offset === 0) {
        await loadPolicies();
      } else {
        setOffset(0);
      }
    } catch (err) {
      setCreateError(err?.message || "Failed to create policy");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div style={{ display: "grid", gap: spacing.lg }}>
      <div>
        <h2 style={{ marginBottom: 6 }}>Policies</h2>
        <p style={{ marginTop: 0, color: "#6b7280", fontSize: 14 }}>
          Track tenant policy status and published readiness.
        </p>
      </div>

      <form
        onSubmit={handleSearchSubmit}
        style={{ display: "flex", gap: spacing.md, flexWrap: "wrap", alignItems: "center" }}
      >
        <Input
          value={queryInput}
          onChange={(e) => setQueryInput(e.target.value)}
          placeholder="Search policies"
          style={{ minWidth: 220 }}
        />
        <Select
          label="Status"
          value={statusFilter}
          onChange={(event) => {
            setOffset(0);
            setStatusFilter(event.target.value);
          }}
          style={{ minWidth: 140 }}
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="retired">Retired</option>
        </Select>
        <Select
          label="Publication"
          value={publishedFilter}
          onChange={(event) => {
            setOffset(0);
            setPublishedFilter(event.target.value);
          }}
          style={{ minWidth: 160 }}
        >
          <option value="">All publication states</option>
          <option value="published">Published</option>
          <option value="unpublished">Unpublished</option>
        </Select>
        <Select
          label="Review due"
          value={reviewDueFilter}
          onChange={(event) => {
            setOffset(0);
            setReviewDueFilter(event.target.value);
          }}
          style={{ minWidth: 140 }}
        >
          <option value="">All</option>
          <option value="due">Due</option>
          <option value="not_due">Not due</option>
        </Select>
        <Button type="submit" variant="secondary">
          Search
        </Button>
        <Button type="button" variant="secondary" onClick={handleClearFilters}>
          Clear
        </Button>
        <Button type="button" variant="secondary" onClick={loadPolicies}>
          Refresh
        </Button>
        <Button type="button" variant="primary" onClick={() => setShowAddModal(true)}>
          Add policy
        </Button>
      </form>

      {!loading && !error && (
        <div style={{ color: "#6b7280", fontSize: 13 }}>
          Showing {policies.length} of {total} policies
        </div>
      )}

      {loading && <Loader label="Loading policies..." />}
      {error && <div style={{ color: "#b91c1c" }}>{error}</div>}
      {documentError && <div style={{ color: "#b91c1c", fontSize: 13 }}>{documentError}</div>}

      {!loading && !error && total === 0 && !hasActiveFilters && (
        <div style={emptyCardStyle}>
          <div style={{ fontWeight: 600 }}>No policies yet</div>
          <div style={{ fontSize: 13, color: "#6b7280" }}>
            Create a policy to start versioning and publication tracking.
          </div>
        </div>
      )}

      {!loading && !error && (total > 0 || hasActiveFilters) && (
        <Table
          columns={columns}
          data={policies}
          emptyState="No matching policies."
          getRowKey={(row) => row.tenant_policy_id ?? row.id}
          onRowClick={(row) => {
            if (!row?.tenant_policy_id) return;
            navigate(`/catalog/policies/${encodeURIComponent(String(row.tenant_policy_id))}`);
          }}
          getRowProps={() => ({
            style: { cursor: "pointer" },
          })}
        />
      )}

      {!loading && !error && (total > 0 || hasActiveFilters) && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: spacing.md,
          }}
        >
          <Button
            variant="secondary"
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            disabled={offset <= 0}
          >
            Previous
          </Button>
          <div style={{ color: "#6b7280", fontSize: 13 }}>
            Page {currentPage} of {totalPages}
          </div>
          <Button
            variant="secondary"
            onClick={() => setOffset(offset + PAGE_SIZE)}
            disabled={offset + PAGE_SIZE >= total}
          >
            Next
          </Button>
        </div>
      )}

      <TenantPolicyDetailDrawer
        tenantPolicyId={drawerPolicyId}
        onClose={() => navigate("/catalog/policies")}
        onPolicyUpdated={loadPolicies}
      />

      <PolicyDocumentModal
        open={Boolean(documentViewer)}
        onClose={() => setDocumentViewer(null)}
        documentName={documentViewer?.version?.document_filename || ""}
        documentMimeType={documentViewer?.version?.document_mime_type || ""}
        documentSizeBytes={documentViewer?.version?.document_size_bytes || 0}
        documentUrl={
          documentViewer?.tenantPolicyId && documentViewer?.version?.id
            ? getTenantPolicyVersionDocumentUrl(documentViewer.tenantPolicyId, documentViewer.version.id)
            : ""
        }
        versionLabel={documentViewer?.version?.version ? `Version ${documentViewer.version.version}` : ""}
      />

      <Modal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add policy"
        description="Create a tenant-scoped policy."
        size="md"
      >
        <div style={{ display: "grid", gap: spacing.md }}>
          <Input
            label="Name"
            value={createName}
            onChange={(event) => setCreateName(event.target.value)}
            placeholder="Policy name"
          />
          <Input
            label="Description (optional)"
            value={createDescription}
            onChange={(event) => setCreateDescription(event.target.value)}
            placeholder="Short summary for the initial draft"
          />
          <Input
            label="Owner (optional)"
            value={createOwner}
            onChange={(event) => setCreateOwner(event.target.value)}
            placeholder="Username or owner identifier"
          />
          {createError && <div style={{ color: "#b91c1c" }}>{createError}</div>}
          <div style={{ display: "flex", gap: spacing.sm, justifyContent: "flex-end" }}>
            <Button variant="secondary" onClick={() => setShowAddModal(false)} disabled={creating}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleCreate} disabled={creating}>
              {creating ? "Creating..." : "Create"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
